/** Hosted-page OAuth (SIWE happens on Guardius's /auth/authorize page).
 *  The SPA owns only the public-client legs: PKCE, redirect, code exchange.
 *
 *  Access token lives in sessionStorage — a DELIBERATE deviation from the
 *  spec's "held in memory" line: memory-only forces a full re-auth round-trip
 *  on every page refresh; sessionStorage never touches localStorage and clears
 *  on tab close. Flagged in the PR for Greg to confirm.
 *
 *  The org tenant UUID + API base come from runtime config.json. */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  buildAuthorizeUrl,
  decodeJwtClaims,
  generatePkce,
  permissionMatches,
  randomState,
  INVITE_CODE_PATTERN,
  PLATFORM_ID,
} from './oauth';
import { authTrace } from './trace';
import {
  completeWatchtowerCallback,
  consentRedirectUrl,
  dropWatchtowerCeremony,
  matchesWatchtowerCallback,
} from './watchtower';

export interface RuntimeConfig {
  apiUrl: string;
  guardiusUrl: string;
  env: string;
}

export interface AuthState {
  config: RuntimeConfig;
  token: string | null;
  address: string | null;
  /** The product-level org id for the signed-in viewer, resolved from
   *  `GET /whoami` (the trusted authorizer context) AFTER a token is set —
   *  NEVER from client input or the JWT. `null` until resolved (or if the
   *  lookup fails); the header pill renders a placeholder in that case. */
  orgId: string | null;
  canWrite: boolean;
  isOwner: boolean;
  /** Platform-admin gate (Greg-only): holds the literal platform-admin grant
   *  `*:penseur-trading:*:platform:*:admin` — NO tenant segment (the `*` there
   *  is literal, not the viewer's org). Gates the console's Admin surface. */
  isPlatformAdmin: boolean;
  signIn(): Promise<void>;
  signOut(): void;
}

/** The result of a single-invite redemption attempt — either threaded back from
 *  the token exchange (Guardius redeems server-side after sign-in) or returned
 *  by the explicit `redeemInvite` call. `reason` carries the Guardius failure
 *  `code` (invalid_invite | invite_expired | invite_revoked | invite_exhausted)
 *  so the UI can render a specific message via `friendlyInviteError`. */
export interface InviteOutcome {
  status: 'redeemed' | 'already_redeemed' | 'failed';
  reason?: string;
  grantedRoleIds?: string[];
}

/** Extended internal surface: the provider also exposes a callback error so
 *  Login can render it, plus the captured/pending invite code and the latest
 *  redemption outcome. Tasks 3-8 consume only the frozen `AuthState` fields;
 *  `error` and the invite fields are additive and optional for them. */
export interface AuthContextValue extends AuthState {
  error: string | null;
  pendingInvite: string | null;
  inviteOutcome: InviteOutcome | null;
  redeemInvite(code: string): Promise<InviteOutcome>;
  /** Return a still-valid access token for the next API request, refreshing
   *  silently if the current one is missing or about to expire (within
   *  EXPIRY_SKEW_MS). Resolves to `null` when the session is dead (no refresh
   *  token, or the refresh itself failed → user is signed out). The API client
   *  awaits this before attaching the `Authorization` header. */
  ensureFreshToken(): Promise<string | null>;
  /** Force a single-flighted refresh-token rotation. Concurrent callers share
   *  one in-flight request (rotation reuse would revoke the session). Resolves
   *  to the new access token, or `null` on failure (which signs the user out).
   *  Prefer {@link ensureFreshToken} from request paths; this is the lower-level
   *  primitive the proactive timer and visibility listener drive. */
  refreshSession(): Promise<string | null>;
}

/** Map a Guardius invite-failure `reason` to a user-facing message. Shared by
 *  the boot/exchange paths here and Task 3's sign-in UI. Any unknown or absent
 *  reason falls back to the generic invalid-invite copy. */
export function friendlyInviteError(reason?: string): string {
  switch (reason) {
    case 'invite_expired':
      return 'This invite has expired.';
    case 'invite_revoked':
      return 'This invite was revoked.';
    case 'invite_exhausted':
      return 'This invite has no uses left.';
    case 'invalid_invite':
    default:
      return "This invite link isn't valid — check the link or ask for a new one.";
  }
}

// sessionStorage keys. `console.oauth.pending` carries the one-shot PKCE
// verifier + CSRF state + redirect URI, written by signIn() and consumed
// exactly once by the boot callback. `console.token` holds the access token.
const PENDING_KEY = 'console.oauth.pending';
const TOKEN_KEY = 'console.token';
// Holds a captured invite code across the hosted-page round-trip (and across a
// refresh mid-flow). The authoritative redemption is server-side; this stash is
// what `signIn()` threads into the authorize URL.
const INVITE_KEY = 'console.invite';
// Holds the rotating refresh token (same sessionStorage posture as the access
// token: never localStorage, cleared on tab close). Persisted on every grant —
// the authorization_code exchange and each refresh rotation — and consumed by
// the single-flight `refreshSession`. Guardius rotates this on every use with
// reuse-detection, so the OLD value MUST be overwritten the moment a new one
// arrives (a second use of a spent token revokes ALL the user's sessions).
const REFRESH_KEY = 'console.refresh';

// Refresh the access token this far ahead of its `exp`. Covers clock skew plus
// the round-trip, so a request never carries a token that expires in-flight. A
// background-throttled timer or a slept laptop can blow past this; the
// visibility-change top-up and the per-request `ensureFreshToken` recover those.
const EXPIRY_SKEW_MS = 60_000;

interface PendingAuth {
  verifier: string;
  state: string;
  redirectUri: string;
  invite?: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Failure kinds for runtime-config loading. The boot sequence (App.tsx) keys
 *  on these to decide between the local-dev fallback and a loud error screen:
 *
 *    - 'not-found' : HTTP 404 — config genuinely absent (a real deploy always
 *                    ships config.json, so 404 means we're in local dev).
 *    - 'http'      : any other non-2xx (403/500/…) — a deployed config that the
 *                    origin refused or failed to serve. Must surface loudly.
 *    - 'parse'     : 2xx but the body isn't valid JSON. `contentType` carries the
 *                    response's Content-Type so callers can disambiguate the two
 *                    very different causes (see note below).
 *    - 'network'   : the fetch() itself rejected (DNS/offline/CORS). Ambiguous;
 *                    surfaced loudly — a same-origin asset fetch shouldn't reject.
 *
 *  Why 'parse' needs a content-type: in local dev the vite dev server has no
 *  static /config.json, so it falls through to its SPA history fallback and
 *  returns `index.html` with HTTP 200 + Content-Type: text/html. res.json() then
 *  throws on the leading `<!doctype html>` — i.e. an absent config in dev looks
 *  like a 'parse' failure, NOT a 404. We must treat that case as dev-fallback,
 *  but ONLY when the body was text/html (the vite SPA-fallback signature). A real
 *  deployed config.json served as application/json that fails to parse (corrupt
 *  / truncated upload) keeps a non-html content-type and stays loud. */
export type ConfigErrorKind = 'not-found' | 'http' | 'parse' | 'network';

export class ConfigError extends Error {
  constructor(
    public readonly kind: ConfigErrorKind,
    message: string,
    public readonly status?: number,
    public readonly contentType?: string | null,
  ) {
    super(message);
    this.name = 'ConfigError';
  }
}

/** Load deploy-time runtime config injected by the DashboardStack
 *  BucketDeployment (CDK Source.jsonData → /config.json). Throws a typed
 *  {@link ConfigError} carrying the failure kind so the boot sequence can decide
 *  between the local-dev fallback and a loud error screen. */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  let res: Response;
  try {
    res = await fetch('/config.json');
  } catch (e) {
    throw new ConfigError(
      'network',
      `config.json fetch failed: ${e instanceof Error ? e.message : String(e)}`,
    );
  }

  if (!res.ok) {
    const kind: ConfigErrorKind = res.status === 404 ? 'not-found' : 'http';
    throw new ConfigError(kind, `config.json load failed: ${res.status}`, res.status);
  }

  try {
    return (await res.json()) as RuntimeConfig;
  } catch (e) {
    throw new ConfigError(
      'parse',
      `config.json is not valid JSON: ${e instanceof Error ? e.message : String(e)}`,
      res.status,
      res.headers.get('content-type'),
    );
  }
}

/** Derive UI-gating fields from a stored token. Returns nulls / false when the
 *  token is absent, malformed, or expired (expired tokens are cleared by the
 *  caller). `canWrite` is permissive when permissions are truncated: the
 *  server is authoritative and will 403 an unauthorized write, so we never
 *  hide a control the server might allow. */
function deriveState(token: string | null): {
  address: string | null;
  canWrite: boolean;
  isOwner: boolean;
  isPlatformAdmin: boolean;
  expired: boolean;
} {
  if (!token) return { address: null, canWrite: false, isOwner: false, isPlatformAdmin: false, expired: false };
  const claims = decodeJwtClaims(token);
  if (!claims) return { address: null, canWrite: false, isOwner: false, isPlatformAdmin: false, expired: true };
  if (typeof claims.exp === 'number' && claims.exp * 1000 <= Date.now()) {
    return { address: null, canWrite: false, isOwner: false, isPlatformAdmin: false, expired: true };
  }
  // Org-agnostic console: the tenant the permission strings are keyed on comes
  // from the JWT's own `tenant_id` claim (the token's resolved org), NOT from
  // runtime config — config no longer carries a tenant. An absent claim yields
  // an empty tenant segment, which only matches a `*`-tenant grant.
  const tenant = claims.tenant_id ?? '';
  const address = claims.wallet_address ?? claims.sub;
  // Symmetric wildcard match (see oauth.permissionMatches reconciliation note).
  const truncated = claims.permissions_truncated === true;
  const canWrite = truncated
    ? true
    : permissionMatches(claims.permissions, `*:${PLATFORM_ID}:${tenant}:console:settings:write`);
  // Owner gate for the Team surface: holds `org:*:admin` on this org's tenant.
  // Mirrors `canWrite`'s permissive-when-truncated posture — the console-API's
  // owner authorizer is authoritative, so we never hide a control it might allow.
  const isOwner = truncated
    ? true
    : permissionMatches(claims.permissions, `*:${PLATFORM_ID}:${tenant}:org:*:admin`);
  // Platform-admin gate (Greg-only) for the Admin surface. The platform-admin
  // permission is org-agnostic, so its tenant segment is the LITERAL `*` — NOT
  // the viewer's tenant. Mirrors `isOwner`'s permissive-when-truncated posture
  // (the console-API's platform-admin authorizer is authoritative).
  const isPlatformAdmin = truncated
    ? true
    : permissionMatches(claims.permissions, `*:${PLATFORM_ID}:*:platform:*:admin`);
  return { address, canWrite, isOwner, isPlatformAdmin, expired: false };
}

export function AuthProvider({
  config,
  children,
  redirectPath = '/dashboard/',
  intent,
}: {
  config: RuntimeConfig;
  children: ReactNode;
  /** OAuth callback path on this origin (default `/dashboard/`, the main SPA).
   *  The onboarding wizard entry passes `/dashboard/onboarding.html` so the
   *  hosted-page round-trip returns to the wizard, not the console. Guardius
   *  validates the redirect_uri by ORIGIN only (the path is ignored for the
   *  allowed-origin check), so a same-origin path needs no Guardius change. */
  redirectPath?: string;
  /** Sign-in intent forwarded to `/auth/authorize`. The onboarding wizard passes
   *  `'onboarding'` so a 0-membership wallet gets a lobby token (Guardius >=
   *  2.43.0) instead of dead-ending at /auth/no-tenant. Omitted (undefined) for
   *  the main console, where normal membership resolution applies. */
  intent?: 'onboarding';
}) {
  const [token, setToken] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingInvite, setPendingInvite] = useState<string | null>(null);
  const [inviteOutcome, setInviteOutcome] = useState<InviteOutcome | null>(null);

  // One-shot guard for the OAuth callback exchange. Under React StrictMode the
  // boot effect is invoked twice; the first invoke consumes the one-shot pending
  // stash and starts the code exchange, so the second invoke MUST NOT re-enter
  // the callback path (the stash is gone — re-entry would spuriously reject as a
  // state mismatch and clobber a successful sign-in). This ref survives the
  // double-invoke (a module-level flag would leak across remounts/tests, so we
  // scope it per-provider). Because the stash is single-use, a second exchange
  // is impossible, so the resolved token is committed to state regardless of the
  // effect-cleanup `cancelled` flag.
  const exchangeStarted = useRef(false);

  // The same one-shot guard, for the Watchtower connect ceremony's callback. Its
  // stash is single-use too, so StrictMode's second boot invoke must not
  // re-enter (it would find no stash and log a spurious failure).
  const watchtowerStarted = useRef(false);

  // Single-flight guard for `refreshSession`. Holds the one in-flight refresh
  // promise so the proactive timer, the visibility top-up, and per-request
  // `ensureFreshToken` calls all share it — two concurrent refreshes would each
  // POST the same rotating refresh token, and the second use trips Guardius's
  // reuse-detection and revokes EVERY session for the user.
  const refreshing = useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');

      // TRACE: every entry into the auth boot, including the bounce-back landing
      // (Guardius redirects here with ?code, ?error, or — on no_tenant — to the
      // wizard with ?reason=no_tenant). `path` distinguishes console
      // (/dashboard/) from the wizard (/dashboard/onboarding.html).
      authTrace(config.apiUrl, 'boot.enter', {
        intent: intent ?? null,
        hasCode: code !== null,
        hasState: state !== null,
        hasInvite: params.get('invite') !== null,
        oauthError: params.get('error'),
        reason: params.get('reason'),
      });

      // Invite capture runs BEFORE the callback branch so a `?invite=...` link
      // (which carries no code/state) is stashed for the eventual sign-in. A
      // malformed code is surfaced but never stashed. URL stripping is gated on
      // an invite/error param being present so plain loads stay untouched.
      const invite = params.get('invite');
      if (invite !== null) {
        stripQuery();
        if (INVITE_CODE_PATTERN.test(invite)) {
          sessionStorage.setItem(INVITE_KEY, invite);
          if (!cancelled) setPendingInvite(invite);
        } else if (!cancelled) {
          setInviteOutcome({ status: 'failed', reason: 'invalid_invite' });
        }
      } else if (!cancelled) {
        // Refresh mid-flow: restore a previously captured invite from the stash.
        const stashed = sessionStorage.getItem(INVITE_KEY);
        if (stashed) setPendingInvite(stashed);
      }

      // OAuth error bounce-back: Guardius redirects here with `?error=...` (and
      // no code) when pre-auth invite validation or sign-in itself fails.
      const oauthError = params.get('error');
      if (oauthError && !code) {
        stripQuery();
        // …but the Watchtower ceremony bounces back the same way when the user
        // declines (or Guardius refuses) at the authorize step. That is NOT a
        // sign-in failure: it must not raise the console's auth error, and the
        // one-shot ceremony stash has to be released — this is the only exit
        // path that never reaches `completeWatchtowerCallback`. The stash is
        // dropped even when the error carries no (or a stale) `state`, so a
        // declined ceremony can't leave one behind to shadow a later callback.
        const watchtowerDeclined = matchesWatchtowerCallback(state);
        dropWatchtowerCeremony();
        if (watchtowerDeclined) {
          authTrace(config.apiUrl, 'boot.watchtower_declined', { oauthError });
          // Fall through to the token restore: the console session is untouched
          // and the user can retry the ceremony from the Usage tab.
        } else if (oauthError === 'invalid_invite') {
          sessionStorage.removeItem(INVITE_KEY);
          if (!cancelled) {
            setPendingInvite(null);
            setInviteOutcome({ status: 'failed', reason: 'invalid_invite' });
          }
          return;
        } else {
          if (!cancelled) setError('Sign-in failed: ' + oauthError);
          return;
        }
      }

      // Watchtower connect ceremony callback. This MUST be discriminated before
      // the sign-in branch below: both legs land here with `?code&state`, and
      // the sign-in branch consumes its own one-shot stash on entry — so a
      // Watchtower code reaching it would destroy a live sign-in stash AND be
      // exchanged against the wrong platform. `matchesWatchtowerCallback` reads
      // (without consuming) the separate `console.watchtower.pending` stash.
      //
      // Nothing here can sign the user out: this ceremony is an OPTIONAL,
      // additive authorization. Any failure is traced and boot continues with
      // the existing console session intact.
      const isWatchtowerCallback = code !== null && matchesWatchtowerCallback(state);
      if (isWatchtowerCallback) {
        if (watchtowerStarted.current) return;
        watchtowerStarted.current = true;
        stripQuery();
        // The console session token is already in sessionStorage (the ceremony
        // is launched from inside the signed-in console); the boot state restore
        // below hasn't run yet, so read storage rather than React state.
        const sessionToken = sessionStorage.getItem(TOKEN_KEY);
        if (!sessionToken) {
          // Nothing to authenticate the connect call with — drop the ceremony
          // and fall through to a normal boot rather than dead-ending.
          dropWatchtowerCeremony();
          authTrace(config.apiUrl, 'boot.watchtower_no_session');
        } else {
          try {
            const { consentUrl } = await completeWatchtowerCallback(
              config.apiUrl,
              sessionToken,
              code,
            );
            authTrace(config.apiUrl, 'boot.watchtower_connected', { hasConsentUrl: true });
            window.location.assign(consentRedirectUrl(consentUrl));
            return;
          } catch (e) {
            authTrace(config.apiUrl, 'boot.watchtower_connect_failed', {
              error: e instanceof Error ? e.message : String(e),
            });
            // Fall through to the normal token restore — the user stays signed
            // in and can retry the ceremony from the Usage tab.
          }
        }
      }

      // `watchtowerStarted` is also checked here: once the Watchtower leg has run
      // in this provider, any `?code` still visible belongs to THAT ceremony
      // (its stash is already consumed, so `matchesWatchtowerCallback` no longer
      // recognises it) and must never be exchanged as a sign-in code.
      if (!isWatchtowerCallback && !watchtowerStarted.current && code && state) {
        // Run the callback exchange exactly once across StrictMode's double
        // invoke. The very first invoke that reaches here owns the exchange.
        if (exchangeStarted.current) return;
        exchangeStarted.current = true;

        // Read-and-remove the one-shot pending stash. A missing stash or a
        // state mismatch is a hard reject — we must NOT exchange the code.
        const raw = sessionStorage.getItem(PENDING_KEY);
        sessionStorage.removeItem(PENDING_KEY);
        stripQuery();

        let pending: PendingAuth | null = null;
        try {
          pending = raw ? (JSON.parse(raw) as PendingAuth) : null;
        } catch {
          pending = null;
        }

        if (!pending || pending.state !== state) {
          setError('Sign-in could not be verified (state mismatch). Please try again.');
          return;
        }

        try {
          const res = await fetch(`${config.guardiusUrl}/auth/token`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              grant_type: 'authorization_code',
              code,
              redirect_uri: pending.redirectUri,
              code_verifier: pending.verifier,
              platform_id: PLATFORM_ID,
            }),
          });
          if (!res.ok) {
            authTrace(config.apiUrl, 'boot.token_exchange_failed', { status: res.status });
            setError(`Token exchange failed (${res.status}).`);
            return;
          }
          const body = (await res.json()) as {
            access_token?: string;
            refresh_token?: string;
            invite?: InviteOutcome;
          };
          // TRACE: the authoritative outcome — whether a token was minted, the
          // tenant it resolved to (empty/lobby ⇒ no real membership), and the
          // server-side invite redemption result. Token VALUES are never logged.
          {
            const claims = body.access_token ? decodeJwtClaims(body.access_token) : null;
            authTrace(config.apiUrl, 'boot.token_response', {
              status: res.status,
              hasAccessToken: !!body.access_token,
              hasRefresh: !!body.refresh_token,
              inviteOutcome: body.invite
                ? { status: body.invite.status, reason: body.invite.reason ?? null }
                : null,
              tokenTenant: claims?.tenant_id ?? null,
              permCount: Array.isArray(claims?.permissions) ? claims.permissions.length : null,
            });
          }
          if (!body.access_token) {
            setError('Token exchange returned no access token.');
            return;
          }
          // Silent refresh: persist the rotating refresh_token so the proactive
          // timer / per-request `ensureFreshToken` can keep this session alive
          // past the access token's ~15-min lifetime without a re-sign-in.
          sessionStorage.setItem(TOKEN_KEY, body.access_token);
          if (body.refresh_token) sessionStorage.setItem(REFRESH_KEY, body.refresh_token);
          // Commit regardless of `cancelled`: the exchange is one-shot, so the
          // StrictMode cleanup that flipped `cancelled` is followed by a second
          // invoke that no-ops on the guard above — committing here is the only
          // path that lands the token in state.
          setToken(body.access_token);
          // Clear the captured member invite ONLY when THIS exchange actually
          // carried one (i.e. the console redemption — `body.invite` present).
          // The SAME boot/exchange handles the onboarding wizard's lobby
          // round-trip, which returns with NO invite field; clearing
          // unconditionally there wiped the member invite the console had
          // stashed, so the owner's next console sign-in forwarded no invite and
          // Guardius bounced to no_tenant (the real-flow blocker). Gating on
          // `body.invite` lets the invite-less lobby exchange preserve the stash.
          // NOTE: a successful redemption that grants new roles does NOT update
          // the just-minted token's `permissions` (and thus `canWrite`). A
          // silent refresh re-derives the SAME claims this token already carries
          // (it doesn't re-evaluate roles granted after the token was minted), so
          // callers must still prompt a re-sign-in to pick up the new grants.
          if (body.invite) {
            setInviteOutcome(body.invite);
            sessionStorage.removeItem(INVITE_KEY);
            setPendingInvite(null);
          }
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Token exchange failed.');
        }
        return;
      }

      // No callback (or a Watchtower callback that fell through): load any
      // persisted token, clearing it if expired. Under StrictMode the second
      // boot invoke re-runs this with its own (un-cancelled) closure — and by
      // then the Watchtower leg has stripped the query and consumed its stash,
      // so it lands here too and commits the restored session.
      const stored = sessionStorage.getItem(TOKEN_KEY);
      if (stored) {
        const { expired } = deriveState(stored);
        if (expired) {
          sessionStorage.removeItem(TOKEN_KEY);
          if (!cancelled) setToken(null);
        } else if (!cancelled) {
          setToken(stored);
        }
      }
    };

    void boot();
    return () => {
      cancelled = true;
    };
  }, [config]);

  // Single-flight refresh-token rotation. Returns the new access token, or null
  // on a dead session (no refresh token / failed refresh → signed out). All
  // callers funnel through the `refreshing` ref so only one POST is ever in
  // flight against the rotating refresh token.
  const refreshSession = (): Promise<string | null> => {
    if (refreshing.current) return refreshing.current;

    const run = async (): Promise<string | null> => {
      const refreshToken = sessionStorage.getItem(REFRESH_KEY);
      if (!refreshToken) return null;

      const killSession = (): null => {
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_KEY);
        setToken(null);
        return null;
      };

      try {
        const res = await fetch(`${config.guardiusUrl}/auth/token`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ grant_type: 'refresh_token', refresh_token: refreshToken }),
        });
        if (!res.ok) return killSession();
        const body = (await res.json()) as { access_token?: string; refresh_token?: string };
        if (!body.access_token) return killSession();
        // Rotation: persist the NEW refresh token (and overwrite the spent one)
        // BEFORE anything else can read it. A reused refresh token revokes all
        // sessions, so the old value must never survive a successful refresh.
        sessionStorage.setItem(TOKEN_KEY, body.access_token);
        if (body.refresh_token) sessionStorage.setItem(REFRESH_KEY, body.refresh_token);
        setToken(body.access_token);
        return body.access_token;
      } catch {
        // Network rejection (offline/CORS) — treat as a dead session, never
        // retry: a retry could re-send a token the server already rotated.
        return killSession();
      }
    };

    const promise = run().finally(() => {
      refreshing.current = null;
    });
    refreshing.current = promise;
    return promise;
  };

  // The method the API layer calls before each request: hand back a token that
  // is still valid for at least EXPIRY_SKEW_MS, refreshing silently if not.
  const ensureFreshToken = async (): Promise<string | null> => {
    const current = sessionStorage.getItem(TOKEN_KEY);
    const refreshToken = sessionStorage.getItem(REFRESH_KEY);
    // Logged out and nothing to refresh from.
    if (!current && !refreshToken) return null;

    const claims = current ? decodeJwtClaims(current) : null;
    const expMs = typeof claims?.exp === 'number' ? claims.exp * 1000 : null;
    const stale = expMs === null || expMs - Date.now() <= EXPIRY_SKEW_MS;
    // Fail closed: a stale token we can't refresh is never handed back — the
    // contract is "valid for ≥ EXPIRY_SKEW_MS or null", never a known-bad token.
    if (stale) return refreshToken ? refreshSession() : null;
    return current;
  };

  // Proactive refresh timer: keep an idle tab's session alive so background
  // react-query refetches never hit an expired token. Schedule a refresh at
  // (exp - EXPIRY_SKEW_MS); re-armed whenever `token` changes (a fresh token
  // resets the clock) and cleared on unmount. Background-tab throttling can
  // defer this setTimeout — the visibility top-up below recovers that case.
  useEffect(() => {
    if (!token) return;
    const claims = decodeJwtClaims(token);
    if (typeof claims?.exp !== 'number') return;
    const delay = Math.max(0, claims.exp * 1000 - Date.now() - EXPIRY_SKEW_MS);
    const id = setTimeout(() => {
      void refreshSession();
    }, delay);
    return () => clearTimeout(id);
    // refreshSession reads live storage and is single-flighted, so it's safe to
    // omit from deps; re-arming is driven solely by token rotation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Visibility top-up: when a backgrounded/slept tab becomes visible again, the
  // proactive setTimeout may have been throttled past expiry. Re-check on
  // wake and refresh if needed. Fire-and-forget; single-flight dedupes against
  // any in-flight proactive refresh.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void ensureFreshToken().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Org-from-login: once a token is set, resolve the viewer's product-level
  // orgId from `GET /whoami` (the trusted authorizer context). orgId NEVER comes
  // from client input or the JWT — only from this authoritative round-trip. A
  // failed/!ok lookup leaves orgId null (the header pill shows a placeholder).
  useEffect(() => {
    if (!token) {
      setOrgId(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${config.apiUrl}/whoami`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const body = (await res.json()) as { orgId?: string };
        if (!cancelled) setOrgId(body.orgId ?? null);
      } catch {
        /* leave orgId null; header shows a placeholder */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, config.apiUrl]);

  const signIn = async (): Promise<void> => {
    setError(null);
    const { verifier, challenge } = await generatePkce();
    const state = randomState();
    // The SPA lives at /dashboard/ (/ is the landing page), so the OAuth
    // callback must return here, not to the landing. The onboarding wizard
    // overrides `redirectPath` to /dashboard/onboarding.html so its own
    // round-trip returns to the wizard. Guardius matches the bare origin for the
    // allowed-origin check, so the path doesn't affect that.
    const redirectUri = window.location.origin + redirectPath;
    // Read the captured invite from the stash (pendingInvite state mirrors it).
    // Keeping it in PendingAuth is belt-and-suspenders for debugging — the
    // authoritative redemption happens server-side on /auth/authorize.
    const invite = sessionStorage.getItem(INVITE_KEY) ?? undefined;
    const pending: PendingAuth = { verifier, state, redirectUri, invite };
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    const authorizeUrl = buildAuthorizeUrl(config, { challenge, state, redirectUri, invite, intent });
    // TRACE: the exact authorize URL we send the user to — confirms whether the
    // invite + intent are actually forwarded to Guardius (the crux of the
    // owner-sign-in no_tenant question). sendBeacon fires before this navigation.
    authTrace(config.apiUrl, 'signin.redirect', {
      redirectUri,
      hasInvite: !!invite,
      invite: invite ?? null,
      intent: intent ?? null,
      authorizeUrl,
    });
    window.location.assign(authorizeUrl);
  };

  const redeemInvite = async (code: string): Promise<InviteOutcome> => {
    if (!token) return { status: 'failed', reason: 'invalid_invite' };
    try {
      const res = await fetch(`${config.guardiusUrl}/invites/redeem`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ code }),
      });
      const body = (await res.json()) as { status?: string; code?: string; grantedRoleIds?: string[] };
      const outcome: InviteOutcome = res.ok
        ? { status: body.status as InviteOutcome['status'], grantedRoleIds: body.grantedRoleIds }
        : { status: 'failed', reason: body.code ?? 'invalid_invite' };
      setInviteOutcome(outcome);
      // On success the CURRENT token does NOT carry the newly granted
      // permissions (no refresh flow), so callers prompt a re-sign-in.
      if (outcome.status !== 'failed') sessionStorage.removeItem(INVITE_KEY);
      return outcome;
    } catch {
      const outcome: InviteOutcome = { status: 'failed', reason: 'invalid_invite' };
      setInviteOutcome(outcome);
      return outcome;
    }
  };

  const signOut = (): void => {
    sessionStorage.removeItem(PENDING_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(INVITE_KEY);
    setToken(null);
    setError(null);
    setPendingInvite(null);
    setInviteOutcome(null);
  };

  const { address, canWrite, isOwner, isPlatformAdmin } = deriveState(token);

  const value: AuthContextValue = {
    config,
    token,
    address,
    orgId,
    canWrite,
    isOwner,
    isPlatformAdmin,
    error,
    pendingInvite,
    inviteOutcome,
    signIn,
    signOut,
    redeemInvite,
    ensureFreshToken,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/** Strip the OAuth callback query (code/state) from the URL without a reload. */
function stripQuery(): void {
  window.history.replaceState({}, '', window.location.pathname + window.location.hash);
}
